import type { OnInit } from '@angular/core';
import {
  Component,
  forwardRef,
  input,
  model,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { FormValueControl } from '@angular/forms/signals';
import { isNumber, isObject, isString } from 'lodash';
import { Duration } from 'luxon';
import { ONYX_FOCUSABLE } from '../../../directives/onyx-auto-focus.directive';
import { OnyxBaseFormControlComponent } from '../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';
import type { OnyxTime, OnyxTimeRange } from '../interfaces';
import type { TimepickerInputValue } from './onyx-timepicker-input/onyx-timepicker-input.component';
import { OnyxTimepickerInputComponent } from './onyx-timepicker-input/onyx-timepicker-input.component';

type Value = OnyxTime | OnyxTimeRange | number | null;

@Component({
  selector: 'onyx-timepicker',
  imports: [OnyxTimepickerInputComponent, OnyxErrorComponent],
  templateUrl: './onyx-timepicker.component.html',
  styleUrl: './onyx-timepicker.component.scss',
  host: { '[style.width]': `width()` },
  providers: [
    {
      provide: ONYX_FOCUSABLE,
      useExisting: forwardRef(() => OnyxTimepickerComponent),
    },
  ],
})
export class OnyxTimepickerComponent
  extends OnyxBaseFormControlComponent
  implements FormValueControl<Value>, OnInit
{
  private readonly timepickerInputRef = viewChild(OnyxTimepickerInputComponent);

  public readonly value = model<Value>(null);
  public readonly label = input<string>();
  public readonly hint = input<OnyxTooltipContext>();
  public readonly labelTo = input<string>();
  public readonly variant = input<'time' | 'duration'>('time');
  public readonly size = input<32 | 40>(40);
  public readonly showTimeRange = input(false);
  public readonly showClockIcon = input(true);
  public readonly forceError = input(false);
  public readonly showErrors = input(true);
  public readonly width = input('100%');

  protected value_ = signal<{
    from?: TimepickerInputValue;
    to?: TimepickerInputValue;
  }>({});

  public override ngOnInit(): void {
    super.ngOnInit();

    toObservable(this.value, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.transformValue(value));
  }

  public focus(): void {
    if (this.disabled()) return;

    this.timepickerInputRef()?.focus();
  }

  protected changeValue(
    type: keyof OnyxTimeRange,
    time: TimepickerInputValue,
  ): void {
    this.value_.update((value) => ({
      ...value,
      [type]: time,
    }));
    this.setValue();
  }

  private transformValue(value: Value): void {
    if (value == null) {
      this.value_.set({});
    } else if (this.showTimeRange() && isObject(value)) {
      value = value as OnyxTimeRange;
      this.value_.set({
        from: this.fromString(value.from),
        to: this.fromString(value.to),
      });
    } else if (isString(value)) {
      this.value_.set({ from: this.fromString(value) });
    } else if (isNumber(value)) {
      this.value_.set({ from: this.fromSeconds(value) });
    }
  }

  private setValue(): void {
    const from = this.fromInput(this.value_().from);
    const value = from != null ? from : null;

    if (this.variant() === 'duration' || !this.showTimeRange()) {
      this.value.set(value);
      return;
    }

    const to = this.fromInput(this.value_().to);
    const rangeValue = from != null && to != null ? { from, to } : null;
    this.value.set(rangeValue as OnyxTimeRange);
  }

  private fromSeconds(seconds: number): TimepickerInputValue {
    const duration = Duration.fromObject({ seconds })
      .shiftTo('hours', 'minutes')
      .normalize();
    return { hour: duration.hours, minutes: duration.minutes };
  }

  private fromString(value: OnyxTime): TimepickerInputValue {
    const [hour, minutes] = value.split(':').map(Number);
    return { hour, minutes };
  }

  private fromInput(value?: TimepickerInputValue): OnyxTime | number | null {
    if (value?.hour == null || value?.minutes == null) return null;

    if (this.variant() === 'duration') {
      return Duration.fromObject(value).as('seconds');
    }

    return `${String(value.hour).padStart(2, '0')}:${String(
      value.minutes,
    ).padStart(2, '0')}:00`;
  }
}
